import React from 'react';
import { DataContext } from '../DataProvider/DataProvider';
import styled from 'styled-components';

function PgnViewer() {
  const {
    pgnTreeRoot,
    currentNode,
    navigateToNode,
    repertoires,
    currentRepertoireId,
    currentMode,
    deleteMove,
  } = React.useContext(DataContext);

  const [contextMenu, setContextMenu] = React.useState({
    visible: false,
    x: 0,
    y: 0,
    targetNode: null,
    isDeleting: false,
  });

  const currentRepertoire = repertoires.find(
    (r) => r.id === currentRepertoireId
  );

  const handleContextMenu = (event, node) => {
    event.preventDefault();
    const x = Math.min(event.clientX, window.innerWidth - 160);
    const y = Math.min(event.clientY, window.innerHeight - 50);
    const finalX = x > 0 ? x : window.innerWidth / 2;
    const finalY = y > 0 ? y : window.innerHeight / 2;

    setContextMenu({
      visible: true,
      x: finalX,
      y: finalY,
      targetNode: node,
      isDeleting: false,
    });
  };

  const handleDeleteMove = async () => {
    if (contextMenu.targetNode) {
      setContextMenu((prev) => ({ ...prev, isDeleting: true }));

      const success = await deleteMove(contextMenu.targetNode);
      if (!success) {
        console.error('Failed to delete move');
        alert('Failed to delete move. Cannot delete the root position.');
      }
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      targetNode: null,
      isDeleting: false,
    });
  };

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-context-menu]')) {
      return;
    }

    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      targetNode: null,
      isDeleting: false,
    });
  };

  React.useEffect(() => {
    if (contextMenu.visible) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [contextMenu.visible]);

  const renderLine = (startNode, isFirstMoveOfVariation = false) => {
    const elements = [];
    let tempNode = startNode;
    let isStart = isFirstMoveOfVariation;

    while (tempNode) {
      const nodeForClick = tempNode;

      const move = nodeForClick.move;
      let moveStr = '';

      if (move.color === 'w') {
        moveStr += `${nodeForClick.moveNumber}. `;
      } else if (isStart) {
        moveStr += `${nodeForClick.moveNumber}... `;
      }

      elements.push(
        <Move
          key={nodeForClick.fen}
          $active={nodeForClick === currentNode}
          onClick={() => navigateToNode(nodeForClick)}
          onContextMenu={(e) => handleContextMenu(e, nodeForClick)}
          title={nodeForClick.comment || undefined}
        >
          {moveStr}
          {move.san}
          {nodeForClick.comment && <NoteIndicator>*</NoteIndicator>}
        </Move>
      );

      const variations = nodeForClick.children.slice(1);
      if (variations.length > 0) {
        variations.forEach((varNode) => {
          elements.push(
            <VariationMarker key={`var-start-${varNode.fen}`}>
              (
            </VariationMarker>
          );
          elements.push(...renderLine(varNode, true));
          elements.push(
            <VariationMarker key={`var-end-${varNode.fen}`}>)</VariationMarker>
          );
        });
      }

      tempNode = nodeForClick.children[0];
      isStart = false;
    }
    return elements;
  };

  const renderPgn = () => {
    if (!pgnTreeRoot || pgnTreeRoot.children.length === 0) {
      return <EmptyState>Make a move on the board to start.</EmptyState>;
    }

    const elements = [];

    if (pgnTreeRoot.children[0]) {
      elements.push(...renderLine(pgnTreeRoot.children[0], false));
    }

    const startVariations = pgnTreeRoot.children.slice(1);
    if (startVariations.length > 0) {
      startVariations.forEach((varNode) => {
        elements.push(<br key={`br-${varNode.fen}`} />);
        elements.push(
          <VariationMarker key={`var-start-root-${varNode.fen}`}>
            [
          </VariationMarker>
        );
        elements.push(...renderLine(varNode, false));
        elements.push(
          <VariationMarker key={`var-end-root-${varNode.fen}`}>
            ]
          </VariationMarker>
        );
      });
    }

    return elements.map((el, i) => (
      <React.Fragment key={el.key || i}>{el} </React.Fragment>
    ));
  };

  if (currentMode === 'trainer') {
    return null;
  }

  if (!currentRepertoire) {
    return <ViewerWrapper>Please select a repertoire.</ViewerWrapper>;
  }

  return (
    <ViewerWrapper>
      <Header>
        <h4>{currentRepertoire.name}</h4>
        {pgnTreeRoot.comment && <RootNote>{pgnTreeRoot.comment}</RootNote>}
      </Header>
      <MovesContainer>{renderPgn()}</MovesContainer>
      <Footer>
        <CommentArea>
          {currentNode && currentNode.comment
            ? currentNode.comment
            : 'Choose a move to review its note.'}
        </CommentArea>
      </Footer>

      {contextMenu.visible && (
        <ContextMenu
          data-context-menu
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          <ContextMenuItem
            onClick={handleDeleteMove}
            disabled={contextMenu.isDeleting}
          >
            {contextMenu.isDeleting ? 'Deleting move...' : 'Delete move'}
          </ContextMenuItem>
        </ContextMenu>
      )}
    </ViewerWrapper>
  );
}

export default PgnViewer;

const ViewerWrapper = styled.div`
  padding: 22px;
  border: 1px solid var(--border);
  border-radius: 26px;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-shadow: var(--shadow-md);
`;

const Header = styled.div`
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
`;

const MovesContainer = styled.div`
  flex-grow: 1;
  font-family: 'Noto Serif JP', serif;
  line-height: 2;
  white-space: pre-wrap;
  overflow-y: auto;
  color: var(--text-soft);
  font-size: 1.02rem;
`;

const Move = styled.span`
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 8px;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: rgba(159, 109, 76, 0.1);
    color: var(--text);
  }

  ${(props) =>
    props.$active &&
    `
    background-color: var(--accent-soft);
    color: var(--accent-strong);
    font-weight: bold;
  `}
`;

const VariationMarker = styled.span`
  color: var(--text-faint);
`;

const EmptyState = styled.p`
  color: var(--text-faint);
  font-style: italic;
`;

const Footer = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
`;

const CommentArea = styled.div`
  font-size: 0.9rem;
  color: var(--text-soft);
  background: rgba(255, 249, 241, 0.9);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 12px 14px;
`;

const NoteIndicator = styled.span`
  color: var(--accent);
  font-weight: bold;
  margin-left: 4px;
`;

const RootNote = styled.p`
  font-size: 0.9rem;
  color: var(--text-soft);
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid rgba(159, 109, 76, 0.28);
`;

const ContextMenu = styled.div`
  position: fixed;
  background: var(--surface-strong);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 18px 34px rgba(65, 46, 32, 0.16);
  z-index: 9999;
  min-width: 150px;
  pointer-events: auto;
  overflow: hidden;
`;

const ContextMenuItem = styled.div`
  padding: 12px 14px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  font-size: 0.92rem;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  color: var(--danger);

  &:hover {
    background-color: ${(props) =>
      props.disabled ? 'transparent' : 'rgba(158, 98, 82, 0.1)'};
  }

  &:first-child {
    border-radius: 4px 4px 0 0;
  }

  &:last-child {
    border-radius: 0 0 4px 4px;
  }
`;
